import { useEffect, useMemo, useState } from "react";
import { UserRound, Filter, Activity, ArrowUpRight, Undo2 } from "lucide-react";
import { Page, Panel, usePagination, Pagination } from "../lib/ui";
import { api, humanDate, today, rowLink } from "../lib/api";
import { DailyReportButton } from "./DailyReport";

const ENTITY_LABEL = {
  invoice: "Fatura", customer: "Cari", stock: "Stok", record: "Gelir/Gider",
  account: "Hesap", transaction: "Hareket", cheque: "Çek", transfer: "Transfer",
  user: "Kullanıcı", settings: "Ayarlar", auth: "Giriş/Çıkış", report: "Rapor",
  purchase_invoice: "Alış Faturası", service: "Hizmet", price_list: "Fiyat Listesi", note: "Not",
  employee: "Personel", appointment: "Randevu", service_ticket: "Servis", warranty: "Garanti", backup: "Yedek",
};
const ACTION_LABEL = {
  create: "Oluşturma", update: "Güncelleme", delete: "Silme",
  login: "Giriş", export: "Dışa aktarma", convert: "Dönüştürme",
  archive: "Arşive taşıma", unarchive: "Arşivden çıkarma",
  cancel: "İptal", uncancel: "İptal geri alma", undo: "Geri alma", transfer: "Transfer",
};
const ACTION_TONE = {
  create: "info", update: "pending", delete: "danger", login: "success",
  export: "neutral", convert: "info", archive: "neutral", unarchive: "neutral",
  cancel: "danger", uncancel: "pending", undo: "pending",
};

const ENTITY_PATH = {
  invoice: "fatura", transaction: "hareket", cheque: "cek",
  record: "kayit", stock: "stok", service: "hizmet",
  work_order: "is-fisi",
};

export default function ActivityLog({ onReload }) {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState({ total: 0, by_user: [], by_entity: [], by_action: [] });
  const [date, setDate] = useState(today());
  const [userId, setUserId] = useState("");
  const [entity, setEntity] = useState("");

  const load = async () => {
    const params = { date, ...(userId && { user_id: userId }), ...(entity && { entity }) };
    const [{ data: list }, { data: sum }] = await Promise.all([
      api.get("/activity", { params }),
      api.get("/activity/summary", { params: { date } }),
    ]);
    setItems(list.items);
    setUsers(list.users);
    setSummary(sum);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, userId, entity]);
  const pg = usePagination(items);

  const undo = async (a, force = false) => {
    if (!force && !window.confirm(`Bu işlem geri alınsın mı?\n\n${a.description || ""}\n\nBağlı bakiye, stok ve hareketler de işlemden önceki haline döner.`)) return;
    try {
      const { data } = await api.post(`/activity/${a.id}/undo`, { force });
      alert(`İşlem geri alındı (${data.changed} kayıt eski haline döndü).`);
      load();
      onReload?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.conflicts) {
        if (window.confirm(`${d.message}\n\nSonraki işlemler:\n- ${d.conflicts.join("\n- ")}\n\nYine de geri alınsın mı?`)) undo(a, true);
      } else alert(typeof d === "string" ? d : "İşlem geri alınamadı");
    }
  };

  return (
    <>
      <Page
        eyebrow="YÖNETİM · AKTİVİTE"
        title="Kullanıcı Hareketleri"
        subtitle="Kullanıcıların günlük tüm işlemleri, giriş kayıtları ve toplu aksiyonlar."
        action={<DailyReportButton />}
      />

      <div className="stat-strip">
        <div className="stat-cell">
          <span>Toplam işlem</span>
          <strong data-testid="activity-total">{summary.total}</strong>
        </div>
        <div className="stat-cell">
          <span>En aktif kullanıcı</span>
          <strong data-testid="activity-top-user">{summary.by_user[0]?.name || "-"}</strong>
          <small>{summary.by_user[0]?.count || 0} işlem</small>
        </div>
        <div className="stat-cell">
          <span>En çok kullanılan modül</span>
          <strong data-testid="activity-top-entity">{ENTITY_LABEL[summary.by_entity[0]?.entity] || "-"}</strong>
          <small>{summary.by_entity[0]?.count || 0} işlem</small>
        </div>
      </div>

      <Panel className="module-panel"
        action={
          <div className="filter-row">
            <label className="field inline-field">
              <span>Tarih</span>
              <input data-testid="activity-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
            <label className="field inline-field">
              <span>Kullanıcı</span>
              <select data-testid="activity-user" value={userId} onChange={(e) => setUserId(e.target.value)}>
                <option value="">Tümü</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </label>
            <label className="field inline-field">
              <span>Modül</span>
              <select data-testid="activity-entity" value={entity} onChange={(e) => setEntity(e.target.value)}>
                <option value="">Tümü</option>
                {Object.entries(ENTITY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
          </div>
        }
      >
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Zaman</th><th>Kullanıcı</th><th>İşlem</th><th>Modül</th><th>Detay</th><th></th></tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan="6" className="empty">Bu tarihte kullanıcı hareketi yok.</td></tr>
              )}
              {pg.slice.map((a) => {
                const path = ENTITY_PATH[a.entity];
                const canOpen = !!path && a.entity_id && a.action !== "delete";
                return (
                  <tr key={a.id} data-testid={`activity-row-${a.id}`}
                      className={canOpen ? "clickable-row" : ""}
                      {...(canOpen ? rowLink(`/detay/${path}/${a.entity_id}`) : {})}>
                    <td>{new Date(a.created_at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}</td>
                    <td><strong>{a.user_name}</strong><small>{a.user_email || "—"}</small></td>
                    <td><span className={`badge tone-${ACTION_TONE[a.action] || "neutral"}`}>{ACTION_LABEL[a.action] || a.action}</span></td>
                    <td>{ENTITY_LABEL[a.entity] || a.entity}</td>
                    <td>
                      {a.description || "-"}
                      {canOpen && <ArrowUpRight size={12} style={{ marginLeft: 6, color: "#17a9d8", verticalAlign: -2 }} />}
                    </td>
                    <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                      {a.undone_at ? (
                        <span data-testid={`activity-undone-${a.id}`} className="badge tone-neutral" title={a.undone_by || ""}>Geri alındı</span>
                      ) : a.undo_id ? (
                        <button type="button" data-testid={`activity-undo-${a.id}`} className="secondary-button"
                                style={{ padding: "4px 10px", fontSize: 12 }} onClick={() => undo(a)}>
                          <Undo2 size={13} /> Geri al
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination page={pg.page} pageCount={pg.pageCount} total={pg.total}
                      onChange={pg.setPage} testid="activity-pagination" />
        </div>
      </Panel>
    </>
  );
}
